import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import { NextSeo } from "next-seo";
import router from "next/router";
import { Col, Row } from "react-styled-flexboxgrid";
import styled from "styled-components";
import Public from "../Layouts/Public";
import PersonalAppFeatures from "../components/AppFeatures/PersonalAppFeatures";
import Button from "../components/Button/Button";
import PageSection from "../components/PageSection";
import Spacer from "../components/Spacer";
import PWAStoreButtons from "../components/pwa/PWAStoreButtons";
import { siteTitle } from "../data/PublicSiteData";
import { trackPageView } from "../includes/analytics";
import { scrollTo } from "../includes/utils";
import { device } from "../theme/Breakpoints";
import { Color } from "../theme/Color";
import { FontSizes } from "../theme/FontSize";

const HeroSection = styled(PageSection)`
  background: linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45)),
    ${Color.White} url(/img/header-backgrounds/homepage.jpg) no-repeat bottom
      center;
  background-size: cover;
`;

export const StyledHeroRow = styled(Row)`
  min-height: calc(100vh - 60px);
`;

export const HeroPreTitleRow = styled(Row)`
  font-size: ${FontSizes[2]};
  margin: 1rem 0;

  @media ${device.lgAndWider} {
    margin-bottom: 3rem;
  }
`;

const Tagline = styled.h1`
  font-size: ${FontSizes[4]};
  font-weight: bold;
  text-transform: uppercase;
  line-height: 150%;
  text-align: left;

  @media ${device.mdOnly} {
    font-size: ${FontSizes[2]};
  }

  @media ${device.lgAndWider} {
    font-size: ${FontSizes[6]};
    margin-bottom: 2rem;
  }
`;

const HeroTitle = styled.h2`
  font-size: ${FontSizes[2]};
  font-weight: bold;
  line-height: 150%;
  text-align: left;

  @media ${device.mdOnly} {
    font-size: ${FontSizes[3]};
  }

  @media ${device.lgAndWider} {
    font-size: ${FontSizes[4]};
    margin-bottom: 1rem;
  }
`;

const StyledH3 = styled.h3`
  font-size: ${FontSizes[1]};
  font-weight: normal;
  line-height: 160%;
  text-align: left;
  margin-bottom: 2rem;

  @media ${device.mdOnly} {
    font-size: ${FontSizes[2]};
  }

  @media ${device.lgAndWider} {
    font-size: ${FontSizes[3]};
  }
`;

const HiddenOnMobile = styled.span`
  display: none;

  @media ${device.mdAndWider} {
    display: inline;
  }
`;

const GoToNextSection = styled.div`
  margin-top: -55px;
  width: 100%;
  text-align: center;
`;

const StyledLink = styled.a`
  text-decoration: underline;
  &:hover {
    cursor: pointer;
  }
`;

const Home = () => {
  const heroImageVariants = {
    initial: { opacity: 0, y: "20vh" },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        type: "spring",
        damping: 12,
        mass: 1,
      },
    },
  };

  const heroContentVariants = {
    initial: { opacity: 0, y: "10vh" },
    animate: {
      opacity: 1,
      y: 0,
    },
  };

  trackPageView("/");

  return (
    <>
      <NextSeo
        title={siteTitle}
        description="Valmeego is a new way for destinations and visitors to interact online.
Get inspiration, local knowledge, advice & deals directly from any destination."
      />

      {/* Hero Section */}
      <HeroSection fullHeight={true}>
        <StyledHeroRow middle="xs">
          {/* Phone Image - Hidden on Mobile */}
          <Col xs={false} sm={4} md={4} lg={4}>
            <Row>
              <Spacer all={2}>
                <motion.img
                  src={`/img/app-screenshots/ask-a-local-ireland-map.png`}
                  alt="Valmeego.com - Locally and beyond"
                  style={{ width: "100%", maxWidth: "338px", height: "auto" }}
                  variants={heroImageVariants}
                  initial="initial"
                  animate="animate"
                />
              </Spacer>
            </Row>
          </Col>

          {/* Dummy Column for layout  */}
          <Col xs={1} sm={false}></Col>

          {/* Homepage text - Everything but mobile */}
          <Col xs={10} sm={6} md={8} lg={8}>
            <Row>
              <Tagline
                as={motion.h1}
                variants={heroContentVariants}
                initial="initial"
                animate="animate"
                transition={{
                  delay: 0.2,
                  duration: 0.4,
                  type: "spring",
                  damping: 12,
                  mass: 1,
                }}
              >
                Find the Ireland for you.
              </Tagline>
              <HeroTitle
                as={motion.h1}
                variants={heroContentVariants}
                initial="initial"
                animate="animate"
                transition={{
                  delay: 0.2,
                  duration: 0.4,
                  type: "spring",
                  damping: 12,
                  mass: 1,
                }}
              >
                Discover the real Ireland for yourself through locally created
                insights.
              </HeroTitle>
            </Row>
            <Row>
              <Col xs={12}>
                <StyledH3
                  as={motion.h3}
                  variants={heroContentVariants}
                  initial="initial"
                  animate="animate"
                  transition={{
                    delay: 0.4,
                    duration: 0.4,
                    type: "spring",
                    damping: 12,
                    mass: 1,
                  }}
                >
                  Engage with trusted, verified Local Advisors who can help you
                  shape a trip tailored to you. Experience the Ireland locals
                  live and love - full of culture, music, food, stories, and
                  hidden places most visitors never see.
                </StyledH3>
              </Col>
            </Row>
            <Row>
              <Spacer bottom={3}>
                <StyledH3
                  as={motion.h3}
                  variants={heroContentVariants}
                  initial="initial"
                  animate="animate"
                  transition={{
                    delay: 0.6,
                    duration: 0.4,
                    type: "spring",
                    damping: 12,
                    mass: 1,
                  }}
                >
                  Connect with a grassroots tourism community helping you get
                  the best out of your time in Ireland. Join for free.
                </StyledH3>
              </Spacer>
            </Row>
            <Row>
              <Col xs={6}>
                <Button
                  dataCy="homepage-hero-login"
                  color="secondary"
                  label="Login"
                  size="extra-large"
                  onClick={() => {
                    router.push("/login");
                  }}
                  as={motion.button}
                  variants={heroContentVariants}
                  initial="initial"
                  animate="animate"
                  transition={{
                    delay: 0.8,
                    duration: 0.4,
                    type: "spring",
                    damping: 12,
                    mass: 1,
                  }}
                />
              </Col>
              <Col
                xs={6}
                as={motion.div}
                variants={heroContentVariants}
                initial="initial"
                animate="animate"
                transition={{
                  delay: 0.8,
                  duration: 0.4,
                  type: "spring",
                  damping: 12,
                  mass: 1,
                }}
              >
                <Button
                  dataCy="homepage-hero-signup-button"
                  color="primary"
                  label="Sign up"
                  size="extra-large"
                  onClick={() => router.push("/personal/register")}
                  icon={<FontAwesomeIcon icon={faChevronRight} />}
                />
                <Row center="xs">
                  <p style={{ textAlign: "center", fontSize: FontSizes[-1] }}>
                    Valmeego is free to join and use
                  </p>
                </Row>
              </Col>
            </Row>

            <PWAStoreButtons />
          </Col>
          <Col xs={1} sm={false}></Col>
        </StyledHeroRow>
        <GoToNextSection>
          <FontAwesomeIcon
            icon={faChevronDown}
            size="2x"
            onClick={() => {
              scrollTo("personal-app-features");
            }}
          />
        </GoToNextSection>
      </HeroSection>

      {/* Features */}
      <PersonalAppFeatures />
    </>
  );
};

Home.Layout = Public;
Home.Title = "Valmeego - Locally and beyond";

export default Home;
